import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { MessageBubble } from '@/components/MessageBubble';
import { apiJson } from '@/lib/api';
import type {
  AdminChatsListResponse,
  AdminUserDetailResponse,
  MessageListResponse,
} from '@/lib/types';

type Thread = 'main' | number;

function AdminUserChatPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? '', [searchStr]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [imageViewerUrl, setImageViewerUrl] = useState<string | null>(null);
  const [thread, setThread] = useState<Thread>('main');

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const hidNum = Number(hid);
  const canView =
    !!hid &&
    Number.isFinite(hidNum) &&
    (userQuery.data?.households.some((h) => h.household_id === hidNum) ?? false);

  useEffect(() => {
    setThread('main');
  }, [hidNum]);

  const customChatsQuery = useQuery({
    queryKey: ['admin', 'household-custom-chats', hidNum],
    queryFn: () => {
      const params = new URLSearchParams({
        household_id: String(hidNum),
        chat_type: 'custom',
        limit: '100',
      });
      return apiJson<AdminChatsListResponse>(
        getToken,
        `/v1/admin/chats?${params.toString()}`,
      );
    },
    enabled: canView,
  });
  const customChats = customChatsQuery.data?.chats ?? [];

  const getMessagesEndpoint = useCallback(
    (beforeTimestamp?: string) => {
      const limit = 50;
      const params = new URLSearchParams({ limit: String(limit) });
      if (beforeTimestamp) params.set('before_timestamp', beforeTimestamp);
      if (thread === 'main') {
        params.set('chat_type', 'main');
        params.set('thread_id', String(hidNum));
      } else {
        params.set('chat_type', 'custom');
        params.set('thread_id', String(thread));
      }
      return `/v1/admin/chats/messages?${params.toString()}`;
    },
    [hidNum, thread],
  );

  const messagesQuery = useInfiniteQuery({
    queryKey: ['admin', 'household-messages', hidNum, thread],
    queryFn: async ({ pageParam }) =>
      apiJson<MessageListResponse>(getToken, getMessagesEndpoint(pageParam ?? undefined)),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.messages.length === 0) return null;
      const oldest = lastPage.messages[lastPage.messages.length - 1];
      return oldest.timestamp;
    },
    refetchInterval: 15000,
    enabled: canView,
  });

  const pages = messagesQuery.data?.pages ?? [];
  const messages = pages.flatMap((p) => p.messages);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.has_more;
  const totalMessageCount = pages[0]?.total_count;

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop < 100 && hasMore && !messagesQuery.isFetchingNextPage) {
      messagesQuery.fetchNextPage();
    }
  }, [hasMore, messagesQuery]);

  const threadLabel =
    thread === 'main'
      ? 'Main chat'
      : (customChats.find((c) => c.thread_id === thread)?.title ?? `Chat #${thread}`);

  if (!hid || !canView) {
    return <p className='text-sm text-nova-muted-foreground'>Select a valid household (use the household chips above).</p>;
  }

  return (
    <div className='flex flex-col min-h-[480px]'>
      <div className='mb-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          {threadLabel} · household #{hidNum}
        </h2>
        {messagesQuery.isLoading ? (
          <span className='text-sm text-nova-muted-foreground tabular-nums'>…</span>
        ) : totalMessageCount != null ? (
          <span className='text-sm font-medium text-nova-muted-foreground tabular-nums'>
            {totalMessageCount.toLocaleString()} message{totalMessageCount === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {customChats.length > 0 ? (
        <div className='mb-3 flex flex-wrap gap-2'>
          <button
            type='button'
            onClick={() => setThread('main')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
              thread === 'main'
                ? 'bg-nova-primary text-white border-nova-primary'
                : 'bg-nova-card text-nova-foreground border-nova-border'
            }`}
          >
            Main chat
          </button>
          {customChats.map((c) => (
            <button
              key={c.thread_id}
              type='button'
              onClick={() => setThread(c.thread_id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border max-w-[200px] truncate ${
                thread === c.thread_id
                  ? 'bg-nova-primary text-white border-nova-primary'
                  : 'bg-nova-card text-nova-foreground border-nova-border'
              }`}
              title={c.title}
            >
              {c.title}
              {c.is_archived ? ' (archived)' : ''}
            </button>
          ))}
        </div>
      ) : null}

      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className='flex-1 overflow-y-auto px-1 py-2 flex flex-col-reverse bg-nova-card rounded-lg border border-nova-border min-h-[360px] max-h-[70vh]'
      >
        {messagesQuery.isLoading ? (
          <div className='flex justify-center py-8'>
            <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
          </div>
        ) : messages.length === 0 ? (
          <p className='text-center text-nova-muted-foreground py-8'>No messages yet.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              currentUserId={id}
              onImagePress={(url) => setImageViewerUrl(url)}
            />
          ))
        )}
      </div>

      {imageViewerUrl ? (
        <button
          type='button'
          className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4'
          onClick={() => setImageViewerUrl(null)}
          aria-label='Close image'
        >
          <FiX className='absolute top-4 right-4 w-8 h-8 text-white' />
          <img src={imageViewerUrl} alt='' className='max-w-full max-h-full object-contain' />
        </button>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/chat')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserChatPage,
});
